import React, { useState, useCallback, useEffect } from 'react'
import moment from 'moment'
import styled from 'styled-components'

import BrowserCompatibility from 'helpers/browserCompatibility'
import URLParamsHelper from 'helpers/url_params_helper'

import { Panel } from 'components/Panel'
import Spinner from 'components/Spinner'
import HelpVideoLink from 'components/HelpVideoLink'

import AwairRthChartNew from './AwairRthChartNew'
import HeaderControls from './HeaderControls'
import RotateDeviceMessage from 'pages/shared/RotateDeviceMessage'

import useServerlessAwairRth from 'hooks/useServerlessAwairRth'
import useOrientation from 'hooks/useOrientation'

import { SelectedLegendItems } from 'types/Awair'

import { screenSize } from 'styles/shared'
import { color } from 'styles/colors'

export type AwairDevice = {
  awairDeviceId: number | null
  name: string
}

type Props = {
  timeZone: string
  deviceId: string
  datesWithData: string[]
  awairDevices?: AwairDevice[]
  awairRthDate?: string | null
}

const AwairRthPanelNew = ({ deviceId, datesWithData, awairDevices = [], timeZone, awairRthDate = null }: Props) => {
  const [selectedAwairDevice, setSelectedAwairDevice] = useState<number | null>(null)

  const [selectedLegendItems, setSelectedLegendItems] = useState<SelectedLegendItems>({
    temp: true,
    humidity: true,
    co2: true,
    voc: true,
    pm25: true,
    actualAirflow: false
  })
  const [selectedDate, setSelectedDate] = useState((awairRthDate ? moment(awairRthDate, 'MM-DD-YYYY') : moment()).format('YYYY-MM-DD'))
  const [displayResetZoom, setDisplayResetZoom] = useState(false)
  const [refreshTimestamp, setRefreshTimestamp] = useState(moment().valueOf())

  const isMobile = BrowserCompatibility.isMobile(navigator.userAgent)
  const { isPortrait } = useOrientation()

  const { awairEvents, displayIndoorUnit, areAwairEventsLoaded, serverErrorOccurred } = useServerlessAwairRth(deviceId, selectedDate, timeZone, refreshTimestamp, selectedAwairDevice)

  const fromTime = moment(selectedDate).startOf('day').format('YYYY-MM-DD HH:mm:ss')
  const toTime = moment(selectedDate).endOf('day').format('YYYY-MM-DD HH:mm:ss')

  useEffect(() => {
    setSelectedAwairDevice(awairDevices.length ? awairDevices[0].awairDeviceId : null)
  }, [deviceId, awairDevices])

  useEffect(() => {
    if (selectedDate) {
      const dateString = moment(selectedDate, 'YYYY-MM-DD').format('MM-DD-YYYY')
      URLParamsHelper.update('awairRthDate', dateString)
    }
  }, [selectedDate])

  const zoomResetCallback = useCallback(() => {
    setDisplayResetZoom(false)
  }, [])

  const refreshCallback = useCallback(() => setRefreshTimestamp(moment().valueOf()), [])

  return (
    <S.Panel id="awair-runtime-history" scrollToSection>
      <S.PanelHeader>
        <S.PanelTitle>Awair Runtime History  <HelpVideoLink videoKey="awair" /></S.PanelTitle>
      </S.PanelHeader>

      {isMobile && isPortrait
        ? (
          <RotateDeviceMessage />
        )
        : (
          <S.Content>
            <HeaderControls
              {...{ displayResetZoom, zoomResetCallback, refreshCallback, datesWithData }}
              dateChangedCallback={setSelectedDate}
              date={selectedDate}
              awairDevicesData={awairDevices}
              awairDeviceSelectionCallback={setSelectedAwairDevice}
            />

            {areAwairEventsLoaded && awairEvents !== null
              ? (
                <AwairRthChartNew
                  {...{ awairEvents, displayIndoorUnit, timeZone, serverErrorOccurred, selectedLegendItems }}
                  date={selectedDate}
                  fromTime={fromTime}
                  toTime={toTime}
                  isZoomed={displayResetZoom}
                  setDisplayResetZoomCallback={setDisplayResetZoom}
                  setSelectedLegendItemsCallback={setSelectedLegendItems}
                />
              )
              : <Spinner variant="page" />
              }
          </S.Content>
        )}
    </S.Panel>
  )
}

export default AwairRthPanelNew

const S = {
  Panel: styled(Panel)`
    min-height: 750px;
    background-color: ${color('paneBackground')};
    @media ${screenSize.small} {
      min-height: 0;
    }
  `,
  PanelHeader: styled(Panel.Header)`
    justify-content: flex-start;
  `,
  PanelTitle: styled(Panel.Title)`
  display:flex;
  align-items: center;
  `,
  Content: styled.div`
    padding: 20px;
  `
}
